import { useMemo } from 'react';
import { goBack, navigate, resetNav } from '../../app/navigation';
import { displayAddress, isInspectionLocked } from '../../domain/inspection';
import { useT, type MessageKey } from '../../i18n/i18n';
import { buildCloseout, rowFactsFrom, type CloseoutGap, type CloseoutGapKind } from '../../state/closeout';
import { buildPreDepartureCheck } from '../../state/preDeparture';
import { aiStore } from '../../state/aiStore';
import { aiJobsStore } from '../../state/aiJobsStore';
import { advancedSummariesOf } from '../../state/rowFacts';
import { useStore } from '../../state/createStore';
import { findingStore } from '../../state/findingStore';
import { inspectionStore } from '../../state/inspectionStore';
import { reportReadinessOf } from '../../state/reportReadiness';
import { ReadinessList } from '../report/ReadinessList';
import { BeforeLeaveSection } from './BeforeLeave';
import { evidenceStore } from '../../state/evidenceStore';
import { nyReportDue } from '../../domain/reportDue';
import { wordingRemindersOf } from '../../report/assembly';
import { Banner, Button, Card, EmptyState, InlineMessage, ListRow, ProgressBar, SectionHeader, TopBar } from '../../ui/components';
import { useStickyFooterClearance } from '../../ui/stickyFooter';
import { formatDate } from '../../ui/hooks';
import { areaDisplayName, itemTitleFor, reportDueMessage, wordingSourceLabel, wordingSuggestion } from '../../i18n/labels';

// Closeout (task book §17): precise gap-filling, not a legal audit. Only genuinely unfinished things
// are listed, each with a Fix → that opens the exact item / finding. AI suggestions are optional and
// never block. When nothing blocks, the way to the report is one tap.
const GAP_TITLE: Record<CloseoutGapKind, MessageKey> = {
  uninspected: 'closeout.gapUninspected',
  unableNoReason: 'closeout.gapUnableNoReason',
  unableNoNote: 'closeout.gapUnableNoNote',
  portionNoNote: 'closeout.gapPortionNoNote',
  defectNoFacts: 'closeout.gapDefectNoFacts',
  defectNoWording: 'closeout.gapDefectNoWording',
  wordingEnglishNotSynced: 'closeout.gapWordingEnglishNotSynced',
  clientEnglishChinese: 'closeout.gapClientEnglishChinese',
  findingNoRecommendation: 'closeout.gapFindingNoRecommendation',
  findingNoTrade: 'closeout.gapFindingNoTrade',
  findingNoPhoto: 'closeout.gapFindingNoPhoto'
};

export function CloseoutScreen(props: { inspectionId: string }) {
  const { t, tp, locale } = useT();
  const footerRef = useStickyFooterClearance();
  const inspectionState = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const inspection = inspectionState.inspections.find((i) => i.id === props.inspectionId) ?? null;
  const aiState = useStore(aiStore, (s) => s);
  const jobs = useStore(aiJobsStore, (s) => s.jobs);
  const evidenceState = useStore(evidenceStore, (s) => s);
  const closeout = useMemo(() => buildCloseout(props.inspectionId, inspectionState, findingState, rowFactsFrom(aiState, evidenceState, advancedSummariesOf(jobs))), [props.inspectionId, inspectionState, findingState, aiState, evidenceState, jobs]);
  // 2026-10-27: 离开前再看一眼 — advisory, never blocking (src/state/preDeparture.ts).
  const departure = useMemo(
    () => buildPreDepartureCheck(props.inspectionId, { inspection: inspectionState, ai: aiState, jobs, evidence: evidenceState, findings: findingState }),
    [props.inspectionId, inspectionState, aiState, jobs, evidenceState, findingState]
  );
  // NY F1 Round 2 (OC-2): the report-readiness gaps are closed HERE, once, before the deliverable.
  const readiness = useMemo(() => reportReadinessOf(props.inspectionId, inspectionState), [props.inspectionId, inspectionState]);
  const wording = useMemo(
    () => wordingRemindersOf(props.inspectionId, { inspection: inspectionState, findings: findingState, evidence: evidenceState }),
    [props.inspectionId, inspectionState, findingState, evidenceState]
  );
  const due = useMemo(
    () => (inspection ? nyReportDue(inspection, inspectionState.areas.filter((a) => a.inspectionId === props.inspectionId), Date.now()) : null),
    [inspection, inspectionState.areas, props.inspectionId]
  );

  if (!inspection) {
    return (
      <div className="screen">
        <TopBar title={t('closeout.title')} onBack={() => resetNav({ screen: 'inspectionList' })} />
        <EmptyState title={t('inspection.notFoundTitle')} body={t('inspection.notFoundBody')} />
      </div>
    );
  }

  const locked = isInspectionLocked(inspection);
  const areaById = new Map(inspectionState.areas.map((a) => [a.id, a] as const));
  const itemById = new Map(inspectionState.items.map((i) => [i.id, i] as const));
  const chineseBlocking = closeout.blocking.filter((g) => g.kind === 'clientEnglishChinese');
  const otherBlocking = closeout.blocking.filter((g) => g.kind !== 'clientEnglishChinese');
  const pct = closeout.progress.total > 0 ? Math.round((closeout.progress.settled / closeout.progress.total) * 100) : 0;

  function fix(gap: CloseoutGap) {
    // the inspection's own text (its notes, its weather) is edited on the inspection details page
    if (gap.areaId === null) navigate({ screen: 'inspectionDetails', inspectionId: props.inspectionId });
    else navigate({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: gap.areaId, itemSheetId: gap.itemId, findingId: gap.findingId });
  }

  function gapRow(gap: CloseoutGap, index: number) {
    const area = gap.areaId !== null ? areaById.get(gap.areaId) : undefined;
    const areaName = area ? areaDisplayName(locale, area) : (gap.areaName ?? t('closeout.inspectionLevel'));
    const item = gap.itemId ? (itemById.get(gap.itemId) ?? null) : null;
    const subject = gap.findingTitle ?? (item ? itemTitleFor(locale, item) : gap.itemTitle) ?? (gap.areaId === null && gap.source ? wordingSourceLabel(t, gap.source) : areaName);
    // Chinese in the English client report: show what prints, so the inspector knows what to rewrite
    const shown = gap.text !== null && gap.source !== null ? t('closeout.gapClientEnglishChineseText', { source: wordingSourceLabel(t, gap.source), text: gap.text.length > 120 ? `${gap.text.slice(0, 120)}…` : gap.text }) : null;
    return (
      <ListRow
        key={`${gap.kind}-${gap.itemId ?? ''}-${gap.findingId ?? ''}-${index}`}
        icon={gap.blocking ? 'priority_high' : 'photo'}
        iconTone={gap.blocking ? 'error' : 'soft'}
        title={subject}
        subtitle={shown ? `${areaName} · ${shown}` : `${areaName} · ${t(GAP_TITLE[gap.kind])}`}
        trailing={<span className="badge badge-muted">{t('closeout.fix')}</span>}
        chevron
        onClick={() => fix(gap)}
        dataName={subject}
        testId={gap.blocking ? 'closeout-blocking-row' : 'closeout-advisory-row'}
      />
    );
  }

  return (
    <div className="screen" data-testid="closeout-screen">
      <TopBar title={t('closeout.title')} subtitle={displayAddress(inspection, t('inspection.noAddress'))} onBack={goBack} />
      <div className="screen-body">
        {/* OD-12 §5.4: New York's five-business-day delivery reminder, in the approved wording.
            It reminds, it never blocks — neither the report nor sharing. */}
        {due ? (
          <p className={`closeout-due ${due.overdue ? 'closeout-due-overdue' : ''}`} data-testid="closeout-due">
            {reportDueMessage(t, due, formatDate(due.dueAt))}
          </p>
        ) : null}
        <Card testId="closeout-progress">
          <div className="progress-card-head">
            <h2 className="section-title">{t('overview.progress')}</h2>
            <span className="progress-pill">{t('common.percent', { n: pct })}</span>
          </div>
          <ProgressBar value={closeout.progress.settled} total={closeout.progress.total} />
          <div className="progress-summary">
            <span>{tp('common.itemsInspected', closeout.progress.total, { done: closeout.progress.settled })}</span>
            <span>{tp('closeout.blockingCount', closeout.blocking.length)}</span>
          </div>
        </Card>

        {locked ? (
          <div className="banner-slot">
            <Banner tone="info" icon="lock" title={t('report.lockedTitle')} body={t('report.lockedBody')} testId="closeout-locked" />
          </div>
        ) : closeout.canFinalize && readiness.ready ? (
          <div className="banner-slot">
            <Banner
              tone="success"
              icon="task_alt"
              title={t('closeout.readyTitle')}
              body={t('closeout.readyBody')}
              testId="closeout-ready"
              action={
                <Button variant="text" size="small" block={false} onClick={() => navigate({ screen: 'report', inspectionId: props.inspectionId })} testId="closeout-go-report">
                  {t('report.open')}
                </Button>
              }
            />
          </div>
        ) : null}

        {otherBlocking.length > 0 ? (
          <div className="list" data-testid="closeout-blocking">
            <SectionHeader title={t('closeout.blockingTitle')} meta={t('closeout.blockingMeta')} />
            {otherBlocking.map(gapRow)}
          </div>
        ) : null}

        {/* E2E walkthrough 2026-10-16: the English client report prints the inspector's own text as
            written. Chinese there blocks; each row shows the text so the inspector knows what to rewrite
            (rows wrap — the text is the point). */}
        {chineseBlocking.length > 0 ? (
          <div className="list list-client-chinese" data-testid="closeout-client-chinese">
            <SectionHeader title={t('closeout.chineseTitle')} meta={t('closeout.chineseMeta')} />
            {chineseBlocking.map(gapRow)}
          </div>
        ) : null}

        {/* 2026-10-27 (AI overnight review): what is easiest to settle while still in the house. Hidden once
            the inspection is finalized — nothing can be changed then. */}
        {!locked ? <BeforeLeaveSection inspectionId={props.inspectionId} check={departure} areas={inspectionState.areas} items={inspectionState.items} /> : null}

        {closeout.advisory.length > 0 ? (
          <div className="list" data-testid="closeout-advisory">
            <SectionHeader title={t('closeout.advisoryTitle')} meta={t('closeout.advisoryMeta')} />
            {closeout.advisory.map(gapRow)}
          </div>
        ) : null}

        {readiness.applies && !readiness.ready ? <ReadinessList inspectionId={props.inspectionId} readiness={readiness} state={inspectionState} testId="closeout-readiness" /> : null}
        {readiness.warnings.includes('licenceMissing') ? <InlineMessage kind="info" text={t('readiness.licenceWarning')} /> : null}

        {/* NY F1 Round 4 §14: wording the report should not carry (code violation, illegal work,
            guarantees …). A reminder list only — it never blocks the report. */}
        {wording.length > 0 ? (
          <div className="list" data-testid="closeout-wording">
            <SectionHeader title={t('wording.title')} meta={tp('wording.reminderCount', wording.length)} />
            <p className="card-note wording-body">{t('wording.body')}</p>
            {wording.map((reminder, index) => (
              <ListRow
                key={`${reminder.phraseId}-${index}`}
                icon="edit_note"
                iconTone="soft"
                title={reminder.sentence}
                subtitle={`${wordingSourceLabel(t, reminder.source)} · ${wordingSuggestion(t, reminder.phraseId)}`}
                chevron={reminder.areaId !== null}
                onClick={reminder.areaId !== null ? () => navigate({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: reminder.areaId as string, itemSheetId: reminder.itemId, findingId: reminder.findingId }) : undefined}
                testId="closeout-wording-row"
              />
            ))}
          </div>
        ) : null}

        <p className="card-note overview-count-note">{t('closeout.aiNote')}</p>
      </div>
      <div ref={footerRef} className="sticky-actions" data-testid="closeout-actions">
        <Button trailingIcon="arrow_forward" onClick={() => navigate({ screen: 'report', inspectionId: props.inspectionId })} disabled={(!closeout.canFinalize || !readiness.ready) && !locked} testId="closeout-continue">
          {locked ? t('report.open') : t('closeout.continueToReport')}
        </Button>
      </div>
    </div>
  );
}
